import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';

//Components

import { ConditionalRendering } from '@components/common/conditional-rendering';
import SideNav from '@components/individualPortal/navigation/SideNavigation';
import NavBarContainer from '../common/NavBarContainer';
import ConfidentialTicketAccessDenied from './forms/ConfidentialTicketAccessDenied';
import TicketQuickActionBar from './ticketQuickActionBar';


//Configs

import { configs } from '@constants/configs/configs';
import {
    getTicketManagementNavLinks,
    ticketManagementTicketDetailNavLinks,
} from '../individualPortal/navigation/configs/ticketManagementSideNavConfigs';

// Hooks

import { useUserPermissions } from '@hooks/useUserPermissions';

// Utils

import { environmentSelector } from '@selectors/commonSelectors';
import { getTicketData } from '@utils/localStorage/sendMessageLocalStorage';

const TicketMgmtContainer = props => {
    const {
        environment: { stateCode, ticketEnabled, user: { activeModuleName, role } = {} },
        ticketSahInfo: { isAuthorisedUser, ticketId, loaded: ticketDataLoaded } = {},
    } = props;
    const currentRoute = useLocation()?.pathname;
    const userPermissions = useUserPermissions();
    const {
        canViewTicketList,
        canViewReports,
        canManageWorkgroup,
        canManageWorkgroupAssignees,
        canManageTicketTypes,
        canManageTags,
        canManageExchangeAssignees,
        canManageCustomFields,
        canManageCannedResponses,
    } = userPermissions;
    const ticketNavLinks = useMemo(
        () => ({
            [stateCode]: getTicketManagementNavLinks({
                canViewTicketList,
                canViewReports,
                canManageWorkgroup,
                canManageWorkgroupAssignees,
                canManageTicketTypes,
                canManageTags,
                canManageExchangeAssignees,
                canManageCustomFields,
                canManageCannedResponses,
            }),
        }),
        [
            stateCode,
            canViewTicketList,
            canViewReports,
            canManageWorkgroup,
            canManageWorkgroupAssignees,
            canManageTicketTypes,
            canManageTags,
            canManageExchangeAssignees,
            canManageCustomFields,
            canManageCannedResponses,
        ],
    );

    const {
        routes: {
            TICKET_MGMT_EXCHANGE_ASSIGNEES,
            TICKET_MGMT_MANAGE_TAGS,
            TICKET_MGMT_MANAGE_CUSTOM_FIELDS,
            TICKET_MGMT_MANAGE_WORKGROUP,
            TICKET_MGMT_MANAGE_CANNED_RESPONSES,
            TICKET_MGMT_WORKGROUP_TAG_EDIT_REDIRECT,
        },
    } = configs;

    const checkForTicketDetailCookie = () => {
        const cookies = `; ${document.cookie}`;
        const parts = cookies.split('; showExtendedMenu=true');
        return parts.length > 1;
    };

    const TICKETDETAILPAGE = checkForTicketDetailCookie();

    const [sideNavData, setSideNavData] = useState({ ...getTicketData() });

    const showTicketMgmtData = !ticketDataLoaded || isAuthorisedUser;

    const headingRenderFunction = () => {
        if (TICKETDETAILPAGE) {
            return (
                <div className="tablet:grid-col-12 ticket-mgmt-qab">
                    <TicketQuickActionBar ticketData={sideNavData} handleChange={setSideNavData} />
                </div>
            );
        } else {
            let messageId;

            const appendix = currentRoute.split('/').pop();

            if (currentRoute.includes(TICKET_MGMT_WORKGROUP_TAG_EDIT_REDIRECT)) {
                messageId = 'ticket_management_header.manageworkgroup';
            } else {
                const routeToMessageIdMap = {
                    [TICKET_MGMT_EXCHANGE_ASSIGNEES]: `ticket_management_header.${appendix}`,
                    [TICKET_MGMT_MANAGE_TAGS]: `ticket_management_header.${appendix}`,
                    [TICKET_MGMT_MANAGE_CUSTOM_FIELDS]: `ticket_management_header.${appendix}`,
                    [TICKET_MGMT_MANAGE_WORKGROUP]: `ticket_management_header.${appendix}`,
                    [TICKET_MGMT_MANAGE_CANNED_RESPONSES]: 'ticket_management_header.manageCannedResponse',
                };

                messageId = routeToMessageIdMap[currentRoute] || 'ticket_management_header.default';
            }

            return (
                <div className="tablet:grid-col-9">
                    <h1 id="skip" className="ticket-mgmt-heading" data-testid="ticket-mgmt-heading">
                        <FormattedMessage id={messageId} />
                    </h1>
                </div>
            );
        }
    };

    const sideNavRenderFunction = () => {
        if (TICKETDETAILPAGE) {
            return (
                <aside className="desktop:grid-col-3" id="ticket-detail-side-nav">
                    <SideNav
                        {...{ stateCode }}
                        navLinks={ticketManagementTicketDetailNavLinks}
                        visibilityParams={sideNavData}
                        outerClass={'ticket-nav'}
                    />
                </aside>
            );
        } else {
            return (
                <aside className="desktop:grid-col-3" id="side-nav">
                    <SideNav
                        {...{ stateCode }}
                        navLinks={ticketNavLinks}
                        visibilityParams={{ ticketEnabled, role, activeModuleName }}
                    />
                </aside>
            );
        }
    };

    return (
        <main className="usa-layout-docs usa-section gi-main-section" id="ticketManagement">
            <div className="grid-container gi-grid-container">
                <div className="grid-row grid-gap gi-wrapper">
                    <div className="grid-col-12">
                        <div className="grid-row">
                            <NavBarContainer navBarClass="ticket-mgmt-top-nav grid-col-12" />
                        </div>
                        <ConditionalRendering isTrue={ticketDataLoaded && !isAuthorisedUser}>
                            <ConfidentialTicketAccessDenied ticketId={ticketId} />
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={showTicketMgmtData}>
                            <div className="grid-row">{headingRenderFunction()}</div>
                        </ConditionalRendering>
                    </div>
                    <ConditionalRendering isTrue={showTicketMgmtData}>
                        <>
                            {sideNavRenderFunction()}
                            <div className="desktop:grid-col-9" id="main-content">
                                <Outlet />
                            </div>
                        </>
                    </ConditionalRendering>
                </div>
            </div>
        </main>
    );
};

const mapStateToProps = state => ({
    environment: environmentSelector(state),
    ticketSahInfo: state.individualPortal.ticketSahInfo,
});

export default connect(mapStateToProps)(TicketMgmtContainer);
